import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  FaBitcoin,
  FaDollarSign,
  FaSterlingSign,
} from "react-icons/fa6";
import { SiTether } from "react-icons/si";

import Preloader from "./components/Preloader";
import {
  useWallet,
  useGetWalletTransactions,
} from "./api/hooks/useWallet";

export default function Transactions({ setSidebarOpen }) {
  /* -------------------- STATE -------------------- */
  const [wallets, setWallets] = useState([]);
  const [activeWallet, setActiveWallet] = useState(null);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    minAmount: "",
    maxAmount: "",
    page: 1,
    perPage: 10,
  });
  const [draftFilters, setDraftFilters] = useState({
    fromDate: "",
    toDate: "",
    minAmount: "",
    maxAmount: "",
    page: 1,
    perPage: 10,
  });

  const [transactions, setTransactions] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState(null);

  /* -------------------- WALLETS -------------------- */
  const { data: walletData, isLoading: walletsLoading } = useWallet();

  useEffect(() => {
    if (walletData?.data?.length) {
      setWallets(walletData.data);
      setActiveWallet(walletData.data[0]);
    }
  }, [walletData]);

  /* -------------------- TRANSACTION PARAMS -------------------- */
  const transactionParams = useMemo(() => {
    if (!activeWallet) return null;

    console.log(filters, "new filters");
    

    return {
      wallet_number_or_uuid:
        activeWallet.wallet_number || activeWallet.id,
      page: filters.page,
      items_per_page: filters.perPage,
      from_date: filters.fromDate || undefined,
      to_date: filters.toDate || undefined,
      min_amount: filters.minAmount || undefined,
      max_amount: filters.maxAmount || undefined,
    };
  }, [activeWallet, filters]);

  /* -------------------- TRANSACTIONS (HOOK) -------------------- */
  const {
    data: transactionsRes,
    isLoading: transactionsLoading,
    error: transactionsError,
    refetch, // optional if you want manual control
  } = useGetWalletTransactions({
    enabled: !!transactionParams,
    ...transactionParams,
  });


  /* -------------------- NORMALIZE TRANSACTIONS -------------------- */
  useEffect(() => {
    const raw =
      transactionsRes?.data?.transactions?.data || [];

    setTransactions(
      raw.map((tx) => ({
        ...tx,
        created_at: tx.created_at || tx.createdAt,
        user_amount: tx.user_amount || tx.amount || 0,
        description: tx.description || tx.narration || "-",
        reference_number:
          tx.reference_number || tx.reference || "-",
        wallet_info: activeWallet,
      }))
    );
  }, [transactionsRes, activeWallet]);

  /* -------------------- HELPERS -------------------- */
  function getWalletIcon(code) {
    if (code === "NGN") return <span>🇳🇬</span>;
    if (code === "USD") return <span>🇺🇸</span>;
    if (code === "GBP") return <FaSterlingSign />;
    if (code === "USDT") return <SiTether />;
    if (["BTC", "ETH", "BNB"].includes(code))
      return <FaBitcoin />;
    return <FaDollarSign />;
  }

  function getStatusDisplay(status) {
    if (typeof status === "object") {
      return status.label || status.slug || "Unknown";
    }
    return status || "Unknown";
  }

  /* -------------------- LOADING / ERROR -------------------- */
  if (walletsLoading) {
    return <Preloader fullScreen message="Loading wallets..." />;
  }

  if (transactionsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <SwapHorizIcon className="text-3xl text-red-500 mb-2" />
          <p className="text-red-600">
            Failed to load transactions
          </p>
        </div>
      </div>
    );
  }

  /* -------------------- RENDER -------------------- */
  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <header className="bg-white border-b px-4 py-4 flex items-center gap-3">
        <button
          onClick={() => setSidebarOpen?.(true)}
          className="xl:hidden"
        >
          <MenuIcon />
        </button>
        <div>
          <h1 className="text-xl font-bold">Transactions</h1>
          <p className="text-sm text-gray-500">
            Wallet transaction history
          </p>
        </div>
      </header>

      <main className="p-6">

        {/* WALLET CAPSULES */}
        <div className="flex gap-3 overflow-x-auto mb-6 pb-2">
          {wallets.map((wallet) => {
            const isActive = activeWallet?.id === wallet.id;
            const code = wallet.currency?.code;

            return (
              <button
                key={wallet.id}
                onClick={() => {
                  setActiveWallet(wallet);
                  setFilters((f) => ({ ...f, page: 1 }));
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border whitespace-nowrap transition
                  ${isActive
                    ? "bg-primary-600 text-white border-primary-600"
                    : "bg-white border-gray-200 hover:bg-gray-100"
                  }`}
              >
                {getWalletIcon(code)}
                <span className="font-semibold text-sm">
                  {code}
                </span>
                <span className="text-xs opacity-70">
                  {wallet.wallet_number}
                </span>
              </button>
            );
          })}
        </div>

        {/* FILTERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <input
            type="date"
            value={draftFilters.fromDate}
            onChange={(e) =>
              setDraftFilters((f) => ({
                ...f,
                fromDate: e.target.value,
              }))
            }
            className="input"
          />

          <input
            type="date"
            value={draftFilters.toDate}
            onChange={(e) =>
              //  console.log(e.target.value)

              setDraftFilters((f) => ({
                ...f,
                toDate: e.target.value,
              }))
            }
            className="input"
          />




          {/* APPLY */}
          <button
            className="btn-primary"
            onClick={() =>

              console.log(draftFilters)

              // setFilters({
              //   ...draftFilters,
              //   page: 1, // always reset page on filter apply
              // })
            }
          >
            Apply
          </button>

          {/* RESET */}
          <button
            className="btn-secondary"
            onClick={() => {
              const reset = {
                fromDate: "",
                toDate: "",
                minAmount: "",
                maxAmount: "",
                page: 1,
                perPage: 10,
              };
              setDraftFilters(reset);
              setFilters(reset);
            }}
          >
            Reset
          </button>
        </div>

        {filters.fromDate || filters.toDate || filters.minAmount || filters.maxAmount ? (
          <span className="text-xs text-primary-600 font-medium">
            Filters applied
          </span>
        ) : null}

        {/* TABLE */}
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm">Date</th>
                <th className="px-4 py-3 text-left text-sm">Amount</th>
                <th className="px-4 py-3 text-left text-sm">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-sm">
                  Reference
                </th>
                <th className="px-4 py-3 text-left text-sm">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {transactionsLoading ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center">
                    <CircularProgress />
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="py-10 text-center text-gray-500"
                  >
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.id} className="border-t">
                    <td className="px-4 py-3 text-sm">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold">
                      {tx.wallet_info?.currency?.symbol}
                      {Number(tx.user_amount).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm truncate max-w-xs">
                      {tx.description}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {tx.reference_number}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {getStatusDisplay(tx.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {transactionsRes?.data?.transactions && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
            {/* Page Info */}
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-medium">
                {(filters.page - 1) * filters.perPage + 1}
              </span>{" "}
              –{" "}
              <span className="font-medium">
                {Math.min(
                  filters.page * filters.perPage,
                  transactionsRes.data.transactions.total
                )}
              </span>{" "}
              of{" "}
              <span className="font-medium">
                {transactionsRes.data.transactions.total}
              </span>{" "}
              results
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <button
                className="btn-secondary"
                disabled={filters.page === 1 || transactionsLoading}
                onClick={() =>
                  setFilters((f) => ({
                    ...f,
                    page: f.page - 1,
                  }))
                }
              >
                Previous
              </button>

              <span className="text-sm font-medium px-2">
                Page {filters.page} of{" "}
                {transactionsRes.data.transactions.last_page}
              </span>

              <button
                className="btn-secondary"
                disabled={
                  filters.page >=
                  transactionsRes.data.transactions.last_page ||
                  transactionsLoading
                }
                onClick={() =>
                  setFilters((f) => ({
                    ...f,
                    page: f.page + 1,
                  }))
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

      </main>

      {/* DETAILS DIALOG (OPTIONAL) */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)}>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {details ? JSON.stringify(details, null, 2) : "-"}
        </DialogContent>
        <DialogActions>
          <button
            className="btn-secondary"
            onClick={() => setDetailsOpen(false)}
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
